import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { getVersionString, getBuildDateString } from "@/constants/version";

type Step = "email" | "code";

const LoginPage = () => {
    const [step, setStep] = useState<Step>("email");
    const [email, setEmail] = useState("");
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const { onSendCode, onVerifyCode, onResendCode } = useAuth();
    const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const handleSendCode = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage("");
        setIsLoading(true);

        try {
            await onSendCode!(email);
            setStep("code");
            setSuccessMessage("Verification code sent to your email");
            setResendCooldown(60);
        } catch (error: any) {
            if (error.message === "Account not found") {
                setErrorMessage("No account found with this email.");
            } else {
                setErrorMessage(error.message || "Failed to send code. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCodeChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newCode = [...code];
        // Handle paste of full code
        if (value.length > 1) {
            const digits = value.replace(/\D/g, "").slice(0, 6).split("");
            digits.forEach((digit, i) => {
                if (i + index < 6) newCode[i + index] = digit;
            });
            setCode(newCode);
            const nextIndex = Math.min(index + digits.length, 5);
            codeInputRefs.current[nextIndex]?.focus();
            return;
        }

        newCode[index] = value;
        setCode(newCode);

        if (value && index < 5) {
            codeInputRefs.current[index + 1]?.focus();
        }
    };

    const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === "Backspace" && !code[index] && index > 0) {
            codeInputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerifyCode = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage("");
        setIsLoading(true);

        const fullCode = code.join("");
        if (fullCode.length !== 6) {
            setErrorMessage("Please enter the full 6-digit code.");
            setIsLoading(false);
            return;
        }

        try {
            await onVerifyCode!(email, fullCode);
        } catch (error: any) {
            if (error.message === "You are not admin") {
                setErrorMessage("Access denied. Admin privileges required.");
            } else {
                setErrorMessage(error.message || "Invalid code. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        setErrorMessage("");
        setSuccessMessage("");

        try {
            await onResendCode!(email);
            setSuccessMessage("New code sent to your email");
            setResendCooldown(60);
            setCode(["", "", "", "", "", ""]);
        } catch (error: any) {
            setErrorMessage(error.message || "Failed to resend code.");
        }
    };

    const handleBackToEmail = () => {
        setStep("email");
        setCode(["", "", "", "", "", ""]);
        setErrorMessage("");
        setSuccessMessage("");
    };

    const inputStyle = {
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4"
            style={{
                background: 'linear-gradient(135deg, #1D2B33 0%, #1D2D36 50%, #162029 100%)',
            }}
        >
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute rounded-full blur-3xl"
                    style={{
                        top: '-10rem',
                        right: '-10rem',
                        width: '20rem',
                        height: '20rem',
                        background: 'rgba(1, 188, 215, 0.15)',
                    }}
                />
                <div
                    className="absolute rounded-full blur-3xl"
                    style={{
                        bottom: '-10rem',
                        left: '-10rem',
                        width: '20rem',
                        height: '20rem',
                        background: 'rgba(79, 158, 215, 0.15)',
                    }}
                />
            </div>

            {/* Login card */}
            <div className="relative w-full max-w-md">
                <div
                    className="rounded-2xl shadow-2xl p-8"
                    style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                >
                    {/* Logo */}
                    <div className="flex justify-center mb-8">
                        <img
                            src="./surfr_ondarkbg.png"
                            alt="Surfr Logo"
                            style={{ height: '64px', width: 'auto' }}
                        />
                    </div>

                    {/* Title */}
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-white mb-2">Surfr UI</h1>
                        <p style={{ color: 'rgba(255, 255, 255, 0.6)' }} className="text-sm">
                            {step === "email"
                                ? "Enter your email to receive a login code"
                                : `Code sent to ${email}`
                            }
                        </p>
                    </div>

                    {/* Success message */}
                    {successMessage && (
                        <div
                            className="rounded-lg px-4 py-3 mb-6"
                            style={{
                                background: 'rgba(34, 197, 94, 0.1)',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                            }}
                        >
                            <p className="text-sm text-center" style={{ color: '#4ade80' }}>
                                {successMessage}
                            </p>
                        </div>
                    )}

                    {step === "email" ? (
                        /* Step 1: Email */
                        <form onSubmit={handleSendCode} className="space-y-6">
                            <div>
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-medium mb-2"
                                    style={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                >
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    required
                                    autoFocus
                                    className="w-full px-4 py-3 rounded-lg text-white transition-all outline-none"
                                    style={inputStyle}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = 'rgb(1, 188, 215)';
                                        e.target.style.boxShadow = '0 0 0 2px rgba(1, 188, 215, 0.3)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                            </div>

                            {/* Error message */}
                            {errorMessage && (
                                <div
                                    className="rounded-lg px-4 py-3"
                                    style={{
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                    }}
                                >
                                    <p className="text-sm text-center" style={{ color: '#f87171' }}>
                                        {errorMessage}
                                    </p>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-3 px-4 text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                style={{
                                    background: 'linear-gradient(135deg, rgb(1, 188, 215) 0%, rgb(1, 165, 192) 100%)',
                                    boxShadow: '0 4px 15px rgba(1, 188, 215, 0.4)',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isLoading) {
                                        e.currentTarget.style.background = 'linear-gradient(135deg, rgb(1, 165, 192) 0%, rgb(1, 188, 215) 100%)';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgb(1, 188, 215) 0%, rgb(1, 165, 192) 100%)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner />
                                        Sending code...
                                    </>
                                ) : (
                                    "Send Login Code"
                                )}
                            </button>
                        </form>
                    ) : (
                        /* Step 2: Code verification */
                        <form onSubmit={handleVerifyCode} className="space-y-6">
                            <div>
                                <label
                                    className="block text-sm font-medium mb-4 text-center"
                                    style={{ color: 'rgba(255, 255, 255, 0.8)' }}
                                >
                                    Enter 6-digit code
                                </label>
                                <div className="flex gap-2 justify-center">
                                    {code.map((digit, index) => (
                                        <input
                                            key={index}
                                            ref={(el) => { codeInputRefs.current[index] = el; }}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={digit}
                                            onChange={(e) => handleCodeChange(index, e.target.value)}
                                            onKeyDown={(e) => handleCodeKeyDown(index, e)}
                                            autoFocus={index === 0}
                                            className="w-12 h-14 text-center text-xl font-bold text-white rounded-lg transition-all outline-none"
                                            style={inputStyle}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = 'rgb(1, 188, 215)';
                                                e.target.style.boxShadow = '0 0 0 2px rgba(1, 188, 215, 0.3)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Error message */}
                            {errorMessage && (
                                <div
                                    className="rounded-lg px-4 py-3"
                                    style={{
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                    }}
                                >
                                    <p className="text-sm text-center" style={{ color: '#f87171' }}>
                                        {errorMessage}
                                    </p>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading || code.join("").length !== 6}
                                className="w-full py-3 px-4 text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                style={{
                                    background: 'linear-gradient(135deg, rgb(1, 188, 215) 0%, rgb(1, 165, 192) 100%)',
                                    boxShadow: '0 4px 15px rgba(1, 188, 215, 0.4)',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isLoading) {
                                        e.currentTarget.style.background = 'linear-gradient(135deg, rgb(1, 165, 192) 0%, rgb(1, 188, 215) 100%)';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgb(1, 188, 215) 0%, rgb(1, 165, 192) 100%)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner />
                                        Verifying...
                                    </>
                                ) : (
                                    "Verify & Sign In"
                                )}
                            </button>

                            {/* Resend & back links */}
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={handleBackToEmail}
                                    className="text-sm transition-colors"
                                    style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'; }}
                                >
                                    Change email
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={resendCooldown > 0}
                                    className="text-sm transition-colors disabled:cursor-not-allowed"
                                    style={{ color: resendCooldown > 0 ? 'rgba(255, 255, 255, 0.3)' : 'rgb(1, 188, 215)' }}
                                    onMouseEnter={(e) => {
                                        if (resendCooldown <= 0) e.currentTarget.style.color = 'rgb(1, 210, 240)';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (resendCooldown <= 0) e.currentTarget.style.color = 'rgb(1, 188, 215)';
                                    }}
                                >
                                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <p
                    className="text-center text-xs mt-6"
                    style={{ color: 'rgba(255, 255, 255, 0.4)' }}
                >
                    {getVersionString()} &middot; {getBuildDateString()}
                </p>
            </div>
        </div>
    );
};

const Spinner = () => (
    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
        <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
        />
        <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
    </svg>
);

export default LoginPage;
